/**
 * Field Type Configuration
 *
 * This file defines the available field types for table schemas,
 * including their display labels and icons.
 */

import {
  Type,
  Hash,
  ToggleLeft,
  Calendar,
  Clock,
  Mail,
  Link,
  Braces,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Field type configuration object
 * Maps data types to their display properties
 */
export interface FieldTypeConfig {
  value: string;
  label: string;
  icon: LucideIcon;
}

/**
 * Available field types for table schemas
 * Each type includes a value (backend identifier), label (display name), and icon
 */
export const FIELD_TYPES: FieldTypeConfig[] = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'boolean', label: 'Boolean', icon: ToggleLeft },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'datetime', label: 'DateTime', icon: Clock },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'url', label: 'URL', icon: Link },
  { value: 'json', label: 'JSON', icon: Braces },
];

/**
 * Get the icon component for a given data type
 * @param dataType - The data type to get the icon for
 * @returns The Lucide icon component for the data type, or Type icon as fallback
 */
export const getTypeIcon = (dataType: string): LucideIcon => {
  const typeConfig = FIELD_TYPES.find((t) => t.value === dataType);
  return typeConfig?.icon ?? Type;
};

/**
 * Get the display label for a given data type
 * @param dataType - The data type to get the label for
 * @returns The display label for the data type, or the dataType itself as fallback
 */
export const getTypeLabel = (dataType: string): string => {
  const typeConfig = FIELD_TYPES.find((t) => t.value === dataType);
  return typeConfig?.label ?? dataType;
};

